/**
 * Etiquetas de los modulos RBAC. FUENTE UNICA DE VERDAD.
 *
 * Cada modulo se llama EXACTAMENTE como la entrada del menu lateral que
 * controla: si el menu dice "TOMA DE ORDENES Y COTIZACIONES", el editor de
 * permisos de un rol no puede llamarlo "Ventas". La clienta veia un nombre en
 * el menu y otro distinto al asignar permisos, y no habia forma de saber que
 * eran el mismo modulo.
 *
 * Antes cada pantalla tenia su propio mapa (RoleForm, RoleDetailModal,
 * Permissions y usePermissions: cuatro copias) y se habian desincronizado.
 * Igual que en config/modulePermissions.js: un solo mapa, y esto no se repite.
 *
 * La clave es el modulo tal como lo guarda la BD (columna `permissions.module`,
 * en MAYUSCULAS, enum `PermissionModule` del backend: 12 valores cerrados).
 * `normalizeModuleName` absorbe mayusculas y alias historicos, y
 * `getModuleDisplayName` es lo unico que deben usar las pantallas.
 */

/** Modulo (normalizado) -> nombre tal cual aparece en el menu lateral. */
export const MODULE_LABELS = {
  // Los 12 modulos del enum PermissionModule del backend.
  dashboard: 'Home',
  ventas: 'Toma de Órdenes y Cotizaciones',
  clientes: 'Clientes',
  productos: 'Productos',
  inventario: 'Inventario',
  proveedores: 'Proveedores',
  ordenes: 'Órdenes de Compra',
  seguimiento: 'Seguimiento de Nuevos Prospectos',
  vendedores: 'Vendedores de Betel',
  produccion: 'Registro de Procesos',
  reportes: 'Reportes Generales',
  admin: 'Administradores',

  // Entradas del menu que NO son un modulo propio: sus permisos viven dentro
  // de otro modulo (ventas.invoices en VENTAS, productos.formulas en
  // PRODUCTOS). Se mapean por si alguna vez llegan como modulo suelto.
  facturacion: 'Facturación',
  formulaciones: 'Formulaciones',
  sistema: 'Sistema',
};

/**
 * Normalizar el modulo: acepta el enum en MAYUSCULAS del backend y los alias
 * en ingles/singular que quedaron de versiones anteriores.
 */
export const normalizeModuleName = (moduleName) => {
  const normalized = (moduleName || 'sistema').toLowerCase().trim();

  const aliases = {
    clients: 'clientes', cliente: 'clientes',
    products: 'productos', producto: 'productos',
    sales: 'ventas', venta: 'ventas',
    inventory: 'inventario',
    suppliers: 'proveedores', proveedor: 'proveedores',
    production: 'produccion',
    sellers: 'vendedores', vendedor: 'vendedores',
    orders: 'ordenes', orden: 'ordenes', compras: 'ordenes',
    reports: 'reportes', reporte: 'reportes',
    tracking: 'seguimiento', prospeccion: 'seguimiento',
    administration: 'admin', users: 'admin', usuarios: 'admin',
    roles: 'admin', permisos: 'admin', permissions: 'admin',
    system: 'sistema', general: 'sistema',
  };

  return aliases[normalized] || normalized;
};

/**
 * Nombre visible de un modulo, igual al del menu lateral. Si llega un modulo
 * desconocido se devuelve capitalizado en vez de romper la pantalla.
 */
export const getModuleDisplayName = (module) => {
  const normalized = normalizeModuleName(module);
  if (MODULE_LABELS[normalized]) return MODULE_LABELS[normalized];

  const raw = (module || '').toString();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : 'Sistema';
};
